import { useId } from 'react'

import type { BrandThemeIcon } from '@jotta/types/Brand'
import { ToggleButton } from '@jotta/ui/ToggleButton'
import { cn } from '@jotta/utils/css'

type ToggleCardProps = {
  disabled?: boolean
  loading?: boolean
  label: string
  checked?: boolean
  defaultChecked?: boolean
  onCheckedChange: (value: boolean) => void
  description?: string
  icon?: BrandThemeIcon
  iconVariant?: 'primary' | 'secondary'
}

export const ToggleCard = ({
  label,
  description,
  disabled = false,
  loading = false,
  checked,
  defaultChecked,
  onCheckedChange,
  icon: Icon,
  iconVariant = 'primary',
}: ToggleCardProps) => {
  const labelId = useId()
  const toggleId = useId()

  return (
    <div className="rounded-xl bg-surface-container-lowest p-4">
      <div className="flex flex-row items-center gap-4">
        {Icon && (
          <div
            className={cn(
              'hidden h-10 w-10 items-center justify-center rounded-full sm:flex',
              {
                'bg-badge-surface-1 text-on-primary': iconVariant === 'primary',
                'bg-outline-light text-on-surface-variant':
                  iconVariant === 'secondary',
              },
            )}
          >
            <Icon width={20} />
          </div>
        )}
        <div className="flex flex-grow flex-col">
          <label id={labelId} htmlFor={toggleId} className="label-md">
            {label}
          </label>
          <span className="mt-1 text-body-sm-ln leading-5 text-outline">
            {description}
          </span>
        </div>
        <ToggleButton
          id={toggleId}
          className="shrink-0"
          disabled={disabled}
          loading={loading}
          aria-labelledby={labelId}
          checked={checked}
          defaultChecked={defaultChecked}
          onCheckedChange={onCheckedChange}
        />
      </div>
    </div>
  )
}
